import { Box, Typography } from '@mui/material';
import PageLoading from '../global/PageLoading';
import FlashbookEmptyState from './FlashbookEmptyState';
import FlashbookDesignsGrid from './FlashbookDesignsGrid';
import { FlashDesignWithRelations } from '../../types/flashbooks';
import useGetFlashbookDesigns from '../../hooks/flashbooks/useGetFlashbookDesigns';

interface Props {
  flashbookId: string | undefined;
  isArchived: boolean;
  onAdd: () => void;
  onOpenSettings?: () => void;
  onSelectDesign: (design: FlashDesignWithRelations) => void;
  onEditDesign?: (design: FlashDesignWithRelations) => void;
  onCopyLinkDesign?: (design: FlashDesignWithRelations) => void;
  isReorderMode?: boolean;
  onReorderChange?: (designs: FlashDesignWithRelations[]) => void;
  reorderedDesigns?: FlashDesignWithRelations[];
}

export default function FlashbookDesignsTab({
  flashbookId,
  isArchived,
  onAdd,
  onOpenSettings,
  onSelectDesign,
  onEditDesign,
  onCopyLinkDesign,
  isReorderMode,
  onReorderChange,
  reorderedDesigns,
}: Props) {
  const { designs, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useGetFlashbookDesigns({
      flashbookId,
      isArchived,
    });

  if (isLoading) return <PageLoading />;

  if (!designs.length) {
    if (isArchived) {
      return (
        <Box py={6} textAlign="center">
          <Typography variant="body1" color="text.secondary">
            No archived designs.
          </Typography>
        </Box>
      );
    }
    return (
      <FlashbookEmptyState onAdd={onAdd} onOpenSettings={onOpenSettings} />
    );
  }

  const visibleDesigns =
    isReorderMode && reorderedDesigns ? reorderedDesigns : designs;

  return (
    <FlashbookDesignsGrid
      designs={visibleDesigns}
      onSelect={onSelectDesign}
      onEdit={isReorderMode ? undefined : onEditDesign}
      onCopyLink={isReorderMode ? undefined : onCopyLinkDesign}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onFetchNextPage={fetchNextPage}
      isReorderMode={isReorderMode}
      onReorderChange={onReorderChange}
    />
  );
}
